import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, NavLink, Outlet, useLoaderData } from '@remix-run/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import RoadmapTodoItem from '#app/components/roadmapTodoItem.js'
import { prisma } from '#app/utils/db.server.ts'
import { cn, getUserImgSrc } from '#app/utils/misc.tsx'
import { getServerAndShardList } from '#app/utils/shards.server.js'
import { useOptionalUser } from '#app/utils/user.ts'

export async function loader({ params }: LoaderFunctionArgs) {
	const owner = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			image: { select: { id: true } },
			shards: { select: { id: true, shard: true } },
		},
		where: { username: params.username },
	})

	invariantResponse(owner, 'Owner not found', { status: 404 })


	const getServers = await getServerAndShardList();

	const serverDeets = getServers.filter((server) => {
		return server.shards.find((shard) => {
			return owner.shards.find((s) => s.shard?.id === shard.id)?.id
		})
	})
	
	const userShards = owner.shards.map((shard) => {
		return {
			...shard,
			server: serverDeets.find((server) => server.shards.find((s) => s.id === shard.shard?.id)),
		}
	})



	return json({ owner, userShards })
}

export default function ShardsRoute() {
	const data = useLoaderData<typeof loader>()
	const ownerDisplayName = data.owner.name ?? data.owner.username
	const navLinkDefaultClassName =
		'line-clamp-2 block rounded-l-full py-2 pl-8 pr-6 text-base lg:text-xl'
	return (
		<main className="container flex h-full min-h-[800px] px-0 pb-12 md:px-8">
			<div className="grid w-full grid-cols-4 bg-muted pl-2 md:container md:rounded-3xl md:pr-0">
				<div className="relative col-span-1">
					<div className="absolute inset-0 flex flex-col">
						<Link
							to={`/users/${data.owner.username}`}
							className="flex flex-col items-center justify-center gap-2 bg-muted pb-4 pl-8 pr-4 pt-12 lg:flex-row lg:justify-start lg:gap-4"
						>
							<img
								src={getUserImgSrc(data.owner.image?.id)}
								alt={ownerDisplayName}
								className="h-16 w-16 rounded-full object-cover lg:h-24 lg:w-24"
							/>
							<h1 className="text-center text-base font-bold md:text-lg lg:text-left lg:text-2xl">
								{ownerDisplayName}'s Shards
							</h1>
						</Link>
						<ul className="overflow-y-auto overflow-x-hidden pb-12">
							{/* {isOwner ? (
								<li className="p-1 pr-0">
									<NavLink
										to={`/users/${data.owner.username}/characters/new`}
										className={({ isActive }) =>
											cn(navLinkDefaultClassName, isActive && 'bg-accent')
										}
									>
										<Icon name="plus">Add a Character</Icon>
									</NavLink>
								</li>
							) : null} */}
							{data.userShards.map((shard) => (
								<li key={shard.id} className="p-1 pr-0">
									<NavLink
										//to={shard.shard?.slug ?? '/servers'}
										to={`/servers/${shard.server?.slug}/shards/${shard.shard?.slug}/players`}
										preventScrollReset
										
										className={({ isActive }) =>
											cn(navLinkDefaultClassName, isActive && 'bg-accent')
										}
									>
										{shard.server?.name} - {shard.shard?.name}
									</NavLink>
								</li>
							))}
						</ul>
					</div>
				</div>
				<div className="relative col-span-3 bg-accent md:rounded-r-3xl">
					<div className='m-4 p-16'>
						<RoadmapTodoItem type="Improved Profile Details" />
					</div>

					<Outlet />
				</div>
			</div>
		</main>
	)
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p>No user with the username "{params.username}" exists</p>
				),
			}}
		/>
	)
}
